import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  Certificate,
  DeclaredCertificate,
  DeviceDirectoryEntry,
  Framework,
  Site,
  VendorDirectoryEntry,
  VendorVerificationStatus,
  VerificationType,
} from '@mvcp/contracts';
import {
  getDeviceDirectory,
  getVendors,
  listApplicableFrameworks,
  listCertificatesForSubject,
  listProposedForSubject,
} from '../api';
import { Logo } from './Logo';
import { BAND_LABEL, BandPill } from './bands';
import { CertificateRows } from './CertificateRows';
import { CreateCertificateForm } from './CreateCertificateForm';
import { deviceLabel } from './device-label';
import { RequestCertificateForm } from './RequestCertificateForm';
import type { DrillTarget } from './CockpitShell';
import { Drawer } from './Drawer';
import { FormModal } from './FormModal';
import { ProposeVendorForm } from './ProposeVendorForm';
// Story 8.9: the pending-proposal marker has ONE owner; this file used to declare its
// own copy (one of three, across eleven use sites).
import { ProposedTag } from './proposed-tag';

/** Rows beyond this count are windowed until the user asks to see all (AD-11). */
const PAGE_SIZE = 50;

/**
 * Verification-status → pill tone. Inlined here (contracts is the source of
 * truth: VENDOR_VERIFICATION_STATUSES + the DESIGN.md status triads) because the
 * web consumes `@mvcp/contracts` as TYPES ONLY — importing a runtime value
 * breaks `vite build` (CJS __exportStar; Story 1.6 debug log). Keep in sync with
 * `packages/contracts/src/entities.ts`.
 */
const TONE: Record<VendorVerificationStatus, 'ok' | 'warn' | 'bad'> = {
  verified: 'ok',
  pending: 'warn',
  unverified: 'bad',
};

/** Human labels for the verification statuses (mirrors the closed contract enum). */
const VERIFICATION_LABEL: Record<VendorVerificationStatus, string> = {
  verified: 'Verified',
  pending: 'Pending',
  unverified: 'Unverified',
};

type ListState =
  | { kind: 'loading' }
  | { kind: 'ready'; entries: VendorDirectoryEntry[] }
  | { kind: 'error'; message: string };

type Filter = 'all' | VendorVerificationStatus;

/**
 * The directory's OUTER selection (Story 7.3): the whole shared backbone, or only
 * the Vendors the caller's own Company uses. `'mine'` groups by the server-computed
 * `usedByCompany` flag — the client NEVER re-derives "my vendors" from a globally
 * read list (NFR-1); it only groups by the boolean the API already scoped.
 */
type Scope = 'mine' | 'all';

/**
 * The ONE toggle-chip renderer behind BOTH toolbar groups: the verification-status
 * filter chips and the Story 7.3 "My vendors" / "All vendors" scope toggle. They are
 * the same kind of control, so they must not merely *look* alike — a restyle of one
 * has to move the other, and `aria-pressed` + the count must be present on both.
 * Accent means "action", never status (the five-tone pill system carries status).
 *
 * EXPORTED as of Story 8.9, which gives the Devices page its own "In use" / "My
 * proposals" scope: a second hand-written chip would be exactly the drift this component
 * was extracted to prevent, one page over.
 *
 * `count` is OPTIONAL as of Story 8.9's review (round 1). The rule used to be "a consumer
 * that cannot supply an honest count yet must not mount it, or the control advertises a
 * zero it invented" — correct about the hazard, wrong about the remedy: not mounting hides
 * the scope toggle at exactly the moment a Manager needs it to reach the explanation for
 * why the count is missing. So `undefined` is now representable and renders an em dash,
 * which claims nothing. A consumer whose source has not honestly settled — in flight, or
 * failed — passes `undefined`; it must NOT pass the length of an array it only has because
 * the failure path emptied it. Every pre-8.9 call site passes a number and is unaffected.
 *
 * KNOWN EXEMPTION, named rather than left as a silent contradiction (review round 2): THIS
 * file's own `scopeCounts` and status chips pass `entries.length` derived from
 * `state.kind === 'ready' ? state.entries : []`, so while `GET /vendors` is in flight or after
 * it fails they do advertise a zero by the rule above. Story 8.9's Never list bars changing
 * Story 7.3's shipped behaviour, so it is recorded here instead of quietly broken — the rule
 * binds NEW consumers, and this page is the one grandfathered caller. Nothing scans for it;
 * whichever story next opens this file should either fix the counts or move this paragraph
 * into the ledger.
 */
export function ToggleChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 12px',
        borderRadius: 'var(--rounded-full)',
        border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
        background: active ? 'var(--accent-soft)' : 'transparent',
        color: active ? 'var(--accent-on-soft)' : 'var(--text-2)',
        fontFamily: 'var(--font-sans)',
        fontWeight: 700,
        fontSize: 13,
        cursor: 'pointer',
      }}
    >
      {label}
      <span data-numeric style={{ opacity: 0.8 }}>
        {count ?? '—'}
      </span>
    </button>
  );
}

function VerificationPill({ status }: { status: VendorVerificationStatus }): React.JSX.Element {
  return (
    <span className="status-pill" data-tone={TONE[status]}>
      <span className="status-pill__dot" aria-hidden="true" />
      {VERIFICATION_LABEL[status]}
    </span>
  );
}

/**
 * Compliance Manager cockpit → Vendors (Story 2.2, FR-8). The Vendor Directory
 * over the shared backbone (AD-19 shared-read — every Company reads the identical
 * tenant-less set), narrowed by three combining controls: a "My vendors" / "All
 * vendors" scope toggle (Story 7.3), a verification-status chip filter and a name
 * search. Scope is the OUTER selection and is driven entirely by the server's
 * per-caller `usedByCompany` flag; it defaults to "All vendors" so the page is
 * unchanged for a Manager who takes no action, and it is suppressed in `readOnly`
 * (admin) mode, where there is no Company for "mine" to mean anything.
 *
 * Each row carries a global verification pill plus neutral seams for certificate
 * status (the canonical `BAND_LABEL.neutral`, Epic 3) and device coverage ("No devices yet",
 * Story 2.6). A row opens a right-side quick-peek Drawer ending in "View full
 * profile", which in standalone mode swaps the pane to a full-page Vendor profile
 * (UX-DR11) and in cockpit mode drills to the enriched {@link VendorDetail}
 * (Story 4.4).
 */
export function VendorDirectory({
  focusId = null,
  onOpenRecord,
  onBack,
  readOnly = false,
}: {
  /**
   * Cockpit drill (Story 4.4): when set, the pane opens the enriched Vendor detail
   * for this id (cert leaf + in-use devices + using-sites) driven by the cockpit
   * back stack. Absent standalone (the Epic-2 list/quick-peek/profile is unchanged).
   */
  focusId?: string | null;
  /** Drill to a spine neighbor (a Vendor's Device / using-Site) — Story 4.4. */
  onOpenRecord?: (target: DrillTarget) => void;
  /** Pop the cockpit back stack (Story 4.4) — the drilled detail's Back. */
  onBack?: () => void;
  /**
   * Read-only directory reach for the System-Administrator Admin Console (Story
   * 6.3, FR-30). When set, suppresses the Compliance-Manager "Propose new vendor"
   * affordance/form (`POST /vendors` is manager-only → 403 for a company-less
   * admin) so the console gets an admin-appropriate browse — no dead manager
   * affordance. Directory curation (verify/dedup/merge) is Story 6.4. Default
   * `false` keeps the manager cockpit call site unchanged.
   */
  readOnly?: boolean;
} = {}): React.JSX.Element {
  const [state, setState] = useState<ListState>({ kind: 'loading' });
  // Story 7.3: the scope defaults to 'all' — this story ADDS a filter to the shipped
  // directory, so a Manager who takes no action must see exactly the pre-7.3 page (and
  // a brand-new Company must not land on an empty list).
  const [scope, setScope] = useState<Scope>('all');
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  // Row currently peeked in the drawer (null = drawer closed).
  const [peekId, setPeekId] = useState<string | null>(null);
  // Row shown as a full-page profile in STANDALONE mode (null = list). In cockpit
  // mode `focusId` drives the detail and this stays null.
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
  // Whether the "Propose new vendor" form is expanded (Story 2.5).
  const [proposing, setProposing] = useState(false);
  const cockpit = onOpenRecord != null;

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const entries = await getVendors();
      setState({ kind: 'ready', entries });
    } catch (err: unknown) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load' });
    }
  }, []);

  /**
   * A NON-DESTRUCTIVE re-read, for the Story 7.3 "Check again" affordance. Unlike
   * {@link load} it never flips to `loading` — in cockpit mode that unmounts
   * {@link VendorDetail} through the loading branch below, discarding the pane's own
   * state and re-fetching every spine section (certificates, declared, devices, sites,
   * frameworks) just to re-read one boolean. A failure is likewise swallowed here rather
   * than replacing the whole pane with the "Couldn't load this vendor" screen: the
   * Manager clicked a small in-section button, so losing the vendor they were reading
   * would be a wildly disproportionate outcome. Returns whether the re-read landed so
   * the caller can acknowledge the result instead of appearing to do nothing.
   *
   * The SUCCESS path has to be guarded too, not just the failure: swapping in a payload
   * that no longer contains the focused Vendor drops the drill through the "Vendor not
   * found" branch below, which is the same pane teardown by another route. That is
   * reachable — an admin merging a proposal DELETEs the vendor row — so a re-read that
   * loses the focused row keeps the entries we already have and reports failure. The
   * not-found branch stays reserved for a stale drill target at MOUNT, which is what it
   * was written for.
   */
  const refresh = useCallback(async (): Promise<boolean> => {
    try {
      const entries = await getVendors();
      if (focusId != null && !entries.some((e) => e.vendor.id === focusId)) return false;
      setState({ kind: 'ready', entries });
      return true;
    } catch {
      return false;
    }
  }, [focusId]);

  useEffect(() => {
    void load();
  }, [load]);

  const entries = state.kind === 'ready' ? state.entries : [];

  // The ONE derivation of "the 'My vendors' scope is in force": `readOnly` (the admin
  // console) has no Company, so it is pinned to the whole directory regardless of state.
  // Everything scope-conditional below reads THIS, never `scope === 'mine'` directly, so
  // the narrowing and the empty state can never disagree about which view is active.
  const mineScope = !readOnly && scope === 'mine';

  // The scope is applied FIRST (it is the outer selection); the status chips and the
  // name search then narrow whatever the scope selected.
  const scoped = useMemo(
    () => (mineScope ? entries.filter((e) => e.usedByCompany) : entries),
    [entries, mineScope],
  );

  // Per-scope counts on the toggle options — ABSOLUTE over the whole loaded set, and
  // deliberately NOT narrowed by the active status chip or search (unlike `counts`
  // below). Each option therefore advertises the size of the scope it selects, which is
  // what keeps "All vendors N" an honest escape hatch beside an empty "My vendors". It
  // is NOT a promise of how many rows will render once a chip or search also applies —
  // with "Pending" active, "All vendors 3" can sit above a shorter table.
  const scopeCounts = useMemo(
    () => ({
      mine: entries.filter((e) => e.usedByCompany).length,
      all: entries.length,
    }),
    [entries],
  );

  // Counts reflect the full IN-SCOPE set (unaffected by the active filter/search): in
  // "My vendors" a chip must promise the rows it will actually show, not a global total.
  const counts = useMemo(
    () => ({
      all: scoped.length,
      verified: scoped.filter((e) => e.vendor.verificationStatus === 'verified').length,
      pending: scoped.filter((e) => e.vendor.verificationStatus === 'pending').length,
      unverified: scoped.filter((e) => e.vendor.verificationStatus === 'unverified').length,
    }),
    [scoped],
  );

  // Distinct existing directory values → autocomplete suggestions for the propose form
  // (Story 2.5 UX). Advisory only; a manager can still type a brand-new primary product.
  // Country and category dropped in DEV-4426 round 2 — both are closed `<select>`s now, and
  // suggesting stored values is what let one typo become the next manager's option.
  const proposeSuggestions = useMemo(() => {
    const distinct = (vals: (string | undefined)[]): string[] =>
      [...new Set(vals.filter((v): v is string => !!v && v.trim() !== ''))].sort((a, b) =>
        a.localeCompare(b),
      );
    return {
      primaryProducts: distinct(entries.map((e) => e.vendor.primaryProduct)),
    };
  }, [entries]);

  const query = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      scoped.filter((e) => {
        const byStatus = filter === 'all' || e.vendor.verificationStatus === filter;
        const byName = query === '' || e.vendor.name.toLowerCase().includes(query);
        return byStatus && byName;
      }),
    [scoped, filter, query],
  );
  const visible = showAll ? filtered : filtered.slice(0, PAGE_SIZE);

  const hasActiveFilter = filter !== 'all' || query !== '';
  // Clear resets the chip filter, the search and the windowing — but NOT the scope
  // (Story 7.3). Scope is the view's outer selection with its own always-visible
  // toggle, so clearing it from here would be the surprising behaviour; both "My
  // vendors" dead ends offer their own one-click escape.
  function clearFilters(): void {
    setFilter('all');
    setSearch('');
    setShowAll(false);
  }

  // A proposal was created: clear any active filter/search first so the new
  // `unverified` row is always visible (a non-matching filter would otherwise
  // make the mutation look like a silent no-op), reload the Directory (scoped to
  // this Company by RLS), and collapse the form. The scope resets to 'all' for the
  // same reason (Story 7.3): a newly proposed Vendor is by definition not yet USED —
  // no site assignment, no install — so leaving the Manager in "My vendors" would
  // hide the row they just created and make a successful mutation look like a no-op.
  const onProposed = useCallback(async () => {
    setScope('all');
    setFilter('all');
    setSearch('');
    setShowAll(false);
    setProposing(false);
    await load();
  }, [load]);

  const peeked = peekId ? entries.find((e) => e.vendor.id === peekId) ?? null : null;
  const selected = selectedVendorId
    ? entries.find((e) => e.vendor.id === selectedVendorId) ?? null
    : null;

  // Both toolbar groups render through the ONE {@link ToggleChip} — same markup, same
  // accessible name shape (`label` + count), so they cannot drift apart visually.
  const chip = (key: Filter, label: string): React.JSX.Element => (
    <ToggleChip
      label={label}
      count={counts[key]}
      active={filter === key}
      onClick={() => {
        setFilter(key);
        setShowAll(false);
      }}
    />
  );

  const scopeOption = (key: Scope, label: string): React.JSX.Element => (
    <ToggleChip
      label={label}
      count={scopeCounts[key]}
      active={scope === key}
      onClick={() => {
        setScope(key);
        setShowAll(false);
      }}
    />
  );

  // ---- Cockpit drill: the enriched Vendor detail for `focusId` (Story 4.4) ----
  if (cockpit && focusId) {
    const back = onBack ?? (() => {});
    const entry = entries.find((e) => e.vendor.id === focusId) ?? null;
    if (state.kind === 'loading') {
      return (
        <>
          <button type="button" className="icon-button" onClick={back} style={{ alignSelf: 'flex-start' }}>
            ← Back
          </button>
          <p className="muted" style={{ margin: 0 }}>Loading…</p>
        </>
      );
    }
    if (state.kind === 'error') {
      // A directory LOAD failure is NOT a missing record — surface the error (never a
      // misleading "no longer exists") with a Retry; Back still works.
      return (
        <>
          <button type="button" className="icon-button" onClick={back} style={{ alignSelf: 'flex-start' }}>
            ← Back
          </button>
          <section className="card stack" role="alert">
            <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>Couldn’t load this vendor</h1>
            <p className="muted" style={{ margin: 0 }}>{state.message}</p>
            <button type="button" className="btn-accent" data-surface="accent" onClick={() => void load()} style={{ alignSelf: 'flex-start' }}>
              Retry
            </button>
          </section>
        </>
      );
    }
    if (!entry) {
      // A stale/unknown drill target: graceful not-found within the pane; Back works.
      return (
        <>
          <button type="button" className="icon-button" onClick={back} style={{ alignSelf: 'flex-start' }}>
            ← Back
          </button>
          <section className="card stack">
            <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>Vendor not found</h1>
            <p className="muted" style={{ margin: 0 }}>This Vendor is no longer in the directory.</p>
          </section>
        </>
      );
    }
    return (
      <VendorDetail
        key={focusId}
        entry={entry}
        onBack={back}
        onOpenRecord={onOpenRecord}
        onRecheck={refresh}
      />
    );
  }

  // ---- Full-page Vendor profile (UX-DR11: NOT a drawer) ----
  if (selected) {
    const v = selected.vendor;
    return (
      <>
        <button
          type="button"
          className="icon-button"
          onClick={() => setSelectedVendorId(null)}
          style={{ alignSelf: 'flex-start' }}
        >
          ← Back to directory
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Logo id={v.id} name={v.name} logo={v.logo} size="lg" />
          <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>
            {v.name}
          </h1>
          <VerificationPill status={v.verificationStatus} />
          {v.proposedByCompanyId ? <ProposedTag /> : null}
        </div>

        <section className="card stack" aria-labelledby="vendor-overview-heading">
          <h2 id="vendor-overview-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Overview
          </h2>
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px', margin: 0 }}>
            <dt className="muted">Category</dt>
            <dd style={{ margin: 0 }}>{v.category ?? '—'}</dd>
            <dt className="muted">Country</dt>
            <dd style={{ margin: 0 }}>{v.country ?? '—'}</dd>
            <dt className="muted">Verification</dt>
            <dd style={{ margin: 0 }}>{VERIFICATION_LABEL[v.verificationStatus]}</dd>
          </dl>
        </section>

        <section className="card stack" aria-labelledby="vendor-certs-heading">
          <h2 id="vendor-certs-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Certificates
          </h2>
          <BandPill band="neutral" />
          <p className="muted" style={{ margin: 0 }}>Certificate scoring arrives in Epic 3.</p>
        </section>

        <section className="card stack" aria-labelledby="vendor-devices-heading">
          <h2 id="vendor-devices-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Devices
          </h2>
          <p className="muted" style={{ margin: 0 }}>No devices yet — the Device Directory arrives in Story 2.6.</p>
        </section>
      </>
    );
  }

  // ---- Directory list ----
  return (
    <>
      <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>Vendors</h1>

      <section className="card stack" aria-labelledby="vendors-heading">
        <div
          style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}
        >
          <h2 id="vendors-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Vendor Directory
          </h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Story 7.3 scope. Suppressed under readOnly: an admin has no Company, so
                "My vendors" would be a permanently empty tab. */}
            {!readOnly && (
              <div role="group" aria-label="Vendor scope" style={{ display: 'flex', gap: 8 }}>
                {scopeOption('mine', 'My vendors')}
                {scopeOption('all', 'All vendors')}
              </div>
            )}
            <div role="group" aria-label="Filter by verification status" style={{ display: 'flex', gap: 8 }}>
              {chip('all', 'All')}
              {chip('verified', 'Verified')}
              {chip('pending', 'Pending')}
              {chip('unverified', 'Unverified')}
            </div>
            <input
              type="search"
              aria-label="Search vendors by name"
              placeholder="Search by name…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setShowAll(false);
              }}
              style={{
                padding: '6px 10px',
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--rounded-md)',
                color: 'var(--text)',
                fontFamily: 'var(--font-sans)',
                fontSize: 14,
              }}
            />
            {hasActiveFilter && (
              <button type="button" className="icon-button" onClick={clearFilters}>
                Clear
              </button>
            )}
            {!readOnly && (
              <button
                type="button"
                className="btn-accent"
                data-surface="accent"
                onClick={() => setProposing((p) => !p)}
                aria-expanded={proposing}
              >
                Propose new vendor
              </button>
            )}
          </div>
        </div>

        {!readOnly && proposing && (
          <FormModal title="Propose a new vendor" titleId="propose-vendor-heading" onClose={() => setProposing(false)} wide>
          <ProposeVendorForm
            onCreated={() => void onProposed()}
            onCancel={() => setProposing(false)}
            suggestions={proposeSuggestions}
          />
          </FormModal>
        )}

        {state.kind === 'loading' && <p className="muted" style={{ margin: 0 }}>Loading…</p>}

        {state.kind === 'error' && <p className="muted" style={{ margin: 0 }}>{state.message}</p>}

        {state.kind === 'ready' && entries.length === 0 && (
          <p className="muted" style={{ margin: 0 }}>No vendors in the directory yet.</p>
        )}

        {/* "My vendors" EMPTY state (Story 7.3): the directory has rows, but this
            Company uses none of them. Distinct from the no-matches state below —
            clearing a filter would not help, so it explains how a Vendor becomes
            "used" and offers the one action that does help. */}
        {state.kind === 'ready' && mineScope && entries.length > 0 && scoped.length === 0 && (
          <div className="stack" style={{ gap: 8 }}>
            <p className="muted" style={{ margin: 0 }}>
              Your company doesn’t use any vendors yet. A vendor becomes one of yours when you
              assign it to one of your sites, or when you install one of its devices.
            </p>
            <button
              type="button"
              className="btn-accent"
              data-surface="accent"
              onClick={() => {
                // Clear the chip/search too, not just the scope: this is the ONE action
                // offered on a dead-end page, so it must not be able to land the Manager
                // in the OTHER dead end ("No vendors match your filter or search") when a
                // status chip or search was still active when they opened "My vendors".
                setScope('all');
                setFilter('all');
                setSearch('');
                setShowAll(false);
              }}
              style={{ alignSelf: 'flex-start' }}
            >
              Show all vendors
            </button>
          </div>
        )}

        {/* NO-MATCHES state: there ARE in-scope rows, but none match the status chip
            or the search — so the toolbar's Clear is the escape, not a scope change. */}
        {state.kind === 'ready' && scoped.length > 0 && filtered.length === 0 && (
          <div className="stack" style={{ gap: 8 }}>
            <p className="muted" style={{ margin: 0 }}>No vendors match your filter or search.</p>
            {!readOnly && !proposing && (
              <button
                type="button"
                className="btn-accent"
                data-surface="accent"
                onClick={() => setProposing(true)}
                style={{ alignSelf: 'flex-start' }}
              >
                Propose new vendor
              </button>
            )}
          </div>
        )}

        {state.kind === 'ready' && filtered.length > 0 && (
          <>
            <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Country</th>
                  <th>Verification</th>
                  <th>Certificates</th>
                  <th>Devices</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((e) => (
                  <tr
                    key={e.vendor.id}
                    onClick={() => setPeekId(e.vendor.id)}
                    data-clickable="true"
                  >
                    <td>
                      <button
                        type="button"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setPeekId(e.vendor.id);
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 10,
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          color: 'var(--text)',
                          font: 'inherit',
                          fontWeight: 600,
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        {/* Identity tile (DESIGN.md avatarPalette) — inside the button so the
                            whole name+tile pair is one target, but aria-hidden: the adjacent
                            name is already the accessible name, and reading the initials
                            first would just stutter it. */}
                        <Logo id={e.vendor.id} name={e.vendor.name} logo={e.vendor.logo} />
                        {e.vendor.name}
                      </button>
                    </td>
                    <td>{e.vendor.category ?? '—'}</td>
                    <td>{e.vendor.country ?? '—'}</td>
                    <td>
                      <span className="cell-cluster">
                        <VerificationPill status={e.vendor.verificationStatus} />
                        {e.vendor.proposedByCompanyId ? <ProposedTag /> : null}
                      </span>
                    </td>
                    <td>
                      <BandPill band="neutral" />
                    </td>
                    <td className="muted">
                      No devices yet
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {filtered.length > PAGE_SIZE && (
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span className="muted" style={{ fontSize: 13 }}>
                  Showing <span data-numeric>{visible.length}</span> of{' '}
                  <span data-numeric>{filtered.length}</span>
                </span>
                {!showAll && (
                  <button type="button" className="icon-button" onClick={() => setShowAll(true)}>
                    Show all
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </section>

      {peeked && (
        <Drawer title={peeked.vendor.name} onClose={() => setPeekId(null)}>
          <span className="cell-cluster">
            <VerificationPill status={peeked.vendor.verificationStatus} />
            {peeked.vendor.proposedByCompanyId ? <ProposedTag /> : null}
          </span>
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px', margin: 0 }}>
            <dt className="muted">Category</dt>
            <dd style={{ margin: 0 }}>{peeked.vendor.category ?? '—'}</dd>
            <dt className="muted">Country</dt>
            <dd style={{ margin: 0 }}>{peeked.vendor.country ?? '—'}</dd>
            <dt className="muted">Certificates</dt>
            <dd style={{ margin: 0 }}>{BAND_LABEL.neutral}</dd>
            <dt className="muted">Devices</dt>
            <dd style={{ margin: 0 }}>No devices yet</dd>
          </dl>
          <button
            type="button"
            className="btn-accent"
            data-surface="accent"
            onClick={() => {
              const id = peeked.vendor.id;
              setPeekId(null);
              // Cockpit: drill through the back stack to the enriched detail; standalone:
              // the in-pane neutral profile (Epic-2 behavior).
              if (onOpenRecord) onOpenRecord({ kind: 'vendor', id });
              else setSelectedVendorId(id);
            }}
          >
            View full profile
          </button>
        </Drawer>
      )}
    </>
  );
}

type DetailState =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      certs: Certificate[];
      /** The manager's OWN pending submissions for this Vendor (Story 6.10). */
      declared: DeclaredCertificate[];
      devices: DeviceDirectoryEntry[];
      sites: Site[];
      frameworks: Framework[];
      frameworkCode: (frameworkId: string) => string;
      frameworkVerificationType: (frameworkId: string) => VerificationType | undefined;
    }
  | { kind: 'error'; message: string };

/**
 * The enriched cockpit Vendor detail (Story 4.4). Consumes only existing read
 * endpoints (no new API/compute): its own certificates (`listCertificatesForSubject`),
 * the in-use Devices it manufactures (`getDeviceDirectory` filtered by `vendor.id`),
 * and the Sites those Devices are installed at (the union of their `sites`).
 * Certificates are the read-only leaf; the Device/Site cross-refs drill on to those
 * records. Header data comes from the already-loaded directory `entry` so it renders
 * immediately; the spine sections load asynchronously with their own
 * loading/error/empty states.
 *
 * NOTE the Sites section is DEVICE-DERIVED only — it never reads `site_vendor`, so a
 * Site↔Vendor assignment does not appear there. It is therefore a narrower fact than
 * `entry.usedByCompany` (Story 7.3), which counts both arms; the section's heading and
 * empty text say so explicitly, because a Manager reading "used by nobody" next to an
 * enabled Add-certificate button would be reading a third, contradictory definition of
 * the vendor↔company relationship.
 */
function VendorDetail({
  entry,
  onBack,
  onOpenRecord,
  onRecheck,
}: {
  entry: VendorDirectoryEntry;
  onBack: () => void;
  onOpenRecord?: (target: DrillTarget) => void;
  /**
   * Re-read the Vendor Directory (Story 7.3). The pane never re-reads `usedByCompany`
   * on its own, so without this a Manager who performs the stated remedy keeps being
   * told to perform it until something else reloads the directory. Resolves `false` if
   * the re-read failed; it must NOT tear this pane down either way (see `refresh`).
   */
  onRecheck: () => Promise<boolean>;
}): React.JSX.Element {
  const v = entry.vendor;
  const [detail, setDetail] = useState<DetailState>({ kind: 'loading' });
  // Whether the "Add certificate" form is expanded (Story 6.8).
  const [adding, setAdding] = useState(false);
  // Whether the "Request certificate from vendor" form is expanded (Story 6.9).
  const [requesting, setRequesting] = useState(false);
  // Bumped to force a certificate re-read after a create/attach (Story 6.8).
  const [reloadToken, setReloadToken] = useState(0);
  // Outcome of the Story 7.3 "Check again" re-read, announced in a live region. The
  // re-read deliberately does not flicker the pane, and the overwhelmingly likely
  // outcome is "still not used" — which renders IDENTICALLY to the pre-click state — so
  // without an explicit acknowledgement the button is indistinguishable from a dead one.
  const [recheck, setRecheck] = useState<'idle' | 'busy' | 'unchanged' | 'failed'>('idle');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setDetail({ kind: 'loading' });
      try {
        const [certs, declared, dir, frameworks] = await Promise.all([
          listCertificatesForSubject('vendor', v.id),
          // The manager's OWN pending submissions for this Vendor (Story 6.10), shown
          // read-only as "pending verification" alongside the verified backbone certs.
          // AUXILIARY: degrade to an empty list on failure rather than joining the
          // Promise.all rejection — a 500 here must not blank out the certificates,
          // devices and sites, which loaded independently long before 6.10 existed.
          listProposedForSubject('vendor', v.id).catch(() => []),
          getDeviceDirectory(),
          listApplicableFrameworks(),
        ]);
        if (cancelled) return;
        const codeMap = new Map(frameworks.map((f) => [f.id, f.code]));
        // Framework id → verification_type, the orthogonal provenance marker. Company
        // screens carry it for the same reason the vendor portal does: a self-declared
        // certificate can read "Certified", and without the marker that reads as a
        // third-party one (DESIGN.md provenance-marker / PRD §5.5 honesty rule).
        const vtMap = new Map(frameworks.map((f) => [f.id, f.verificationType]));
        // In-use Devices this Vendor manufactures; the using-Sites are the union of
        // those Devices' installed-on Sites (deduped by id, tenant-scoped already).
        const devices = dir.filter((d) => d.vendor.id === v.id);
        const siteById = new Map<string, Site>();
        for (const d of devices) for (const s of d.sites) siteById.set(s.id, s);
        setDetail({
          kind: 'ready',
          certs,
          declared,
          devices,
          sites: [...siteById.values()],
          frameworks,
          frameworkCode: (id) => codeMap.get(id) ?? id,
          frameworkVerificationType: (id) => vtMap.get(id),
        });
      } catch (err: unknown) {
        if (!cancelled) {
          setDetail({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load' });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [v.id, reloadToken]);

  // A certificate was created (or its evidence changed): collapse the form and
  // re-read the subject's certificate list so the new/updated row is reflected.
  const onCertMutated = useCallback(() => {
    setAdding(false);
    setReloadToken((t) => t + 1);
  }, []);

  return (
    <>
      <button type="button" className="icon-button" onClick={onBack} style={{ alignSelf: 'flex-start' }}>
        ← Back
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Logo id={v.id} name={v.name} logo={v.logo} size="lg" />
        <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>{v.name}</h1>
        <VerificationPill status={v.verificationStatus} />
        {v.proposedByCompanyId ? <ProposedTag /> : null}
      </div>

      <section className="card stack" aria-labelledby="vendor-overview-heading">
        <h2 id="vendor-overview-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Overview</h2>
        <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px', margin: 0 }}>
          <dt className="muted">Category</dt>
          <dd style={{ margin: 0 }}>{v.category ?? '—'}</dd>
          <dt className="muted">Country</dt>
          <dd style={{ margin: 0 }}>{v.country ?? '—'}</dd>
          <dt className="muted">Verification</dt>
          <dd style={{ margin: 0 }}>{VERIFICATION_LABEL[v.verificationStatus]}</dd>
        </dl>
      </section>

      <section className="card stack" aria-labelledby="vendor-certs-heading">
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <h2 id="vendor-certs-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Certificates</h2>
          {detail.kind === 'ready' && !adding && !requesting && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {/* Story 7.3 (absorbing the deferred 6.10 review item): submitting a
                  certificate for a Vendor the Company does not use 404s server-side, so
                  the affordance must not exist at all here — a button that fails only
                  after the Manager has filled the form and attached a file is worse than
                  no button. `usedByCompany` is the SAME predicate the endpoint enforces
                  (one shared SQL fragment), so this gate cannot drift from it. */}
              {entry.usedByCompany && (
                <button
                  type="button"
                  className="btn-accent"
                  data-surface="accent"
                  onClick={() => setAdding(true)}
                  aria-expanded={adding}
                >
                  Add certificate
                </button>
              )}
              <button
                type="button"
                className="icon-button"
                onClick={() => {
                  // Opening this form hides the gate block below (it is `!requesting`),
                  // but the block's state outlives the unmount — so without this reset a
                  // "Checked just now…" acknowledgement from before the request flow is
                  // re-rendered, and re-announced by the live region, when the form
                  // closes. Stale by then: no check happened at that moment.
                  setRecheck('idle');
                  setRequesting(true);
                }}
                aria-expanded={requesting}
              >
                Request certificate from vendor
              </button>
            </div>
          )}
        </div>
        {detail.kind === 'loading' && <p className="muted" style={{ margin: 0 }}>Loading…</p>}
        {detail.kind === 'error' && (
          <p role="alert" className="muted" style={{ margin: 0 }}>{detail.message}</p>
        )}
        {/* The gate is never silent: name the reason AND both remedies, at normal body
            contrast — this prose sits INSIDE a populated card beside live buttons, where
            `muted` reads as a footnote (unlike the whole-area empty/no-matches states in
            the list above, which are muted by repo convention because they are the only
            content of an empty region) — plus a real action: the pane holds
            `usedByCompany` from the
            directory read that mounted it, so a Manager who performs the remedy needs a
            way to make this pane notice. Requesting a certificate FROM the vendor stays
            available above — that endpoint needs no shared link. */}
        {detail.kind === 'ready' && !entry.usedByCompany && !requesting && (
          <div className="stack" style={{ gap: 8 }}>
            <p style={{ margin: 0 }}>
              You can’t submit a certificate for this vendor because your company doesn’t use it
              yet. Assign it to one of your sites, or install one of its devices, and you’ll be
              able to add certificates for it. You can still request a certificate from the
              vendor.
            </p>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              Either remedy counts. Only device installs show up under “Sites with this vendor’s
              devices” below.
            </p>
            <button
              type="button"
              className="icon-button"
              disabled={recheck === 'busy'}
              onClick={() => {
                setRecheck('busy');
                void onRecheck().then((ok) => setRecheck(ok ? 'unchanged' : 'failed'));
              }}
              style={{ alignSelf: 'flex-start' }}
            >
              {recheck === 'busy' ? 'Checking…' : 'Check again'}
            </button>
            {/* A successful re-read that LIFTS the gate unmounts this whole block, so a
                message here can only mean "checked, still not used" (or that the read
                failed) — never a stale contradiction of the button above it. The block is
                also unmounted while the request form is open, which does NOT mean a check
                happened, so that path resets `recheck` (see the button above). The region
                is always present so the text is announced when it appears. */}
            <p role="status" className="muted" style={{ margin: 0, fontSize: 13 }}>
              {recheck === 'unchanged'
                ? 'Checked just now — this vendor still isn’t linked to your company.'
                : recheck === 'failed'
                  ? 'Couldn’t check just now — nothing has changed here; try again.'
                  : ''}
            </p>
          </div>
        )}
        {detail.kind === 'ready' && adding && entry.usedByCompany && (
          <FormModal title="Add certificate" titleId="add-cert-heading" onClose={() => setAdding(false)} wide>
          <CreateCertificateForm
            frameworks={detail.frameworks.filter((f) => f.scope === 'company')}
            vendorId={v.id}
            // Story 8.5 (AD-25): model + revision travel; the form composes the label.
            devices={detail.devices.map((d) => ({
              id: d.device.id,
              model: d.device.model,
              ...(d.device.version ? { version: d.device.version } : {}),
              // Story 8.9: a subject that is still this Company's own proposal says so.
              ...(d.device.proposedByCompanyId ? { proposed: true } : {}),
            }))}
            onCreated={onCertMutated}
            onCancel={() => setAdding(false)}
          />
          </FormModal>
        )}
        {detail.kind === 'ready' && requesting && (
          // Both company-scoped (→ the Vendor) and device-scoped (→ one of the Vendor's
          // in-use Devices) frameworks are requestable here; the form gates the subject.
          <FormModal title="Request certificate from vendor" titleId="request-cert-heading" onClose={() => setRequesting(false)} wide>
          <RequestCertificateForm
            frameworks={detail.frameworks}
            vendorId={v.id}
            devices={detail.devices.map((d) => ({
              id: d.device.id,
              model: d.device.model,
              ...(d.device.version ? { version: d.device.version } : {}),
              // Story 8.9: a subject that is still this Company's own proposal says so.
              ...(d.device.proposedByCompanyId ? { proposed: true } : {}),
            }))}
            onDone={() => setRequesting(false)}
            onCancel={() => setRequesting(false)}
          />
          </FormModal>
        )}
        {detail.kind === 'ready' && (
          <CertificateRows
            certificates={detail.certs}
            declared={detail.declared}
            frameworkCode={detail.frameworkCode}
            frameworkVerificationType={detail.frameworkVerificationType}
            manageEvidence
          />
        )}
      </section>

      <section className="card stack" aria-labelledby="vendor-devices-heading">
        <h2 id="vendor-devices-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Devices in use</h2>
        {detail.kind === 'loading' && <p className="muted" style={{ margin: 0 }}>Loading…</p>}
        {detail.kind === 'error' && (
          <p role="alert" className="muted" style={{ margin: 0 }}>{detail.message}</p>
        )}
        {detail.kind === 'ready' &&
          (detail.devices.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>No devices from this vendor are in use.</p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {detail.devices.map((d) => (
                <li key={d.device.id} style={{ borderTop: '1px solid var(--border)', paddingTop: 6 }}>
                  <SpineLink
                    // Story 8.5 (AD-25): the vendor-detail "Devices in use" list names the
                    // revision. It is NOT one of the excluded separate-column renderings —
                    // this list has only a label and a category hint, so before this the
                    // revision appeared nowhere on it and two revisions of one model read
                    // as two identical rows.
                    label={deviceLabel(d.device)}
                    hint={d.device.category ?? undefined}
                    onClick={() => onOpenRecord?.({ kind: 'device', id: d.device.id })}
                  />
                  {/* Story 8.9 / review round 3: the SAME `detail.devices` array feeds the
                      two certificate pickers above, which mark a row that is still this
                      Company's own proposal. This list did not, so one screen gave two
                      answers about one device's curation state — the drift the single-owner
                      refactor exists to remove, at the only place both renderings are
                      visible at once. AC3 names "a device list" alongside the pickers. */}
                  {d.device.proposedByCompanyId && (
                    <>
                      {' '}
                      <ProposedTag />
                    </>
                  )}
                </li>
              ))}
            </ul>
          ))}
      </section>

      <section className="card stack" aria-labelledby="vendor-sites-heading">
        {/* Story 7.3: this section is built from the union of the vendor's INSTALLED
            DEVICES' sites — it never reads `site_vendor`. The old "Used at Sites" heading
            over-claimed, so a Site↔Vendor assignment (which makes `usedByCompany` true
            and lights up Add certificate above) left this reading "not used at any site",
            i.e. two statements contradicting each other on one screen. The heading and
            empty text now say exactly what is listed. */}
        <h2 id="vendor-sites-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
          Sites with this vendor’s devices
        </h2>
        {detail.kind === 'loading' && <p className="muted" style={{ margin: 0 }}>Loading…</p>}
        {detail.kind === 'error' && (
          <p role="alert" className="muted" style={{ margin: 0 }}>{detail.message}</p>
        )}
        {detail.kind === 'ready' &&
          (detail.sites.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>
              None of this vendor’s devices are installed at your sites.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {detail.sites.map((s) => (
                <li key={s.id} style={{ borderTop: '1px solid var(--border)', paddingTop: 6 }}>
                  <SpineLink label={s.name} hint={s.type} onClick={() => onOpenRecord?.({ kind: 'site', id: s.id })} />
                </li>
              ))}
            </ul>
          ))}
      </section>
    </>
  );
}

/**
 * A spine cross-reference link (Story 4.4): a real `<button>` (keyboard-operable,
 * global focus ring) that drills to a neighbor record, with an optional muted hint.
 */
export function SpineLink({
  label,
  hint,
  onClick,
}: {
  label: string;
  hint?: string;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        gap: 8,
        alignItems: 'baseline',
        background: 'none',
        border: 'none',
        padding: 0,
        color: 'var(--text)',
        font: 'inherit',
        fontWeight: 600,
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      {label}
      {hint ? (
        <span className="muted" style={{ fontSize: 13, fontWeight: 400 }}>
          ({hint})
        </span>
      ) : null}
    </button>
  );
}
